"use client";

import { useEffect, useMemo, useState } from "react";
import { Wrench, ListTodo, Send, Loader2, Check } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { AutoTextarea } from "@/components/ui/auto-textarea";
import { Select, type SelectOption } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/date-picker";
import { api } from "@/lib/api";
import { buildDeptTabTree, type TabNode } from "@/lib/dept-tabs";
import type { Department, PortalModule, ChatUser, DevRequestPriority } from "@/lib/types";

/**
 * Кнопки под ответом AI-ассистента: оформить ТЗ как заявку на доработку или
 * как задачу конкретному человеку. Клик открывает модалку-предпросмотр с
 * редактируемыми полями (текст ответа предзаполнен) — запись создаётся только
 * после явного подтверждения, чтобы пользователь сверил правильность.
 */

const PRIOS: { key: DevRequestPriority; label: string; color: string }[] = [
  { key: "low", label: "Низкий", color: "#22c55e" },
  { key: "normal", label: "Обычный", color: "#f59e0b" },
  { key: "high", label: "Высокий", color: "#ef4444" },
];

// Ответ ассистента похож на оформленное ТЗ на доработку — только тогда есть смысл
// предлагать «В доработки»/«Задачей человеку». Системный промпт структурирует ТЗ по
// разделам «Что доработать / Где / Как сейчас / Как должно быть / Приоритет», поэтому
// ищем эти маркеры: обычный Q&A-ответ их не содержит, а ТЗ — почти всегда 2+.
const SPEC_MARKERS = [
  "что доработать", "как сейчас", "как должно быть", "как надо",
  "что сделать", "что нужно сделать", "постановка задачи",
];
export function looksLikeSpec(text: string): boolean {
  const t = text.toLowerCase();
  let hits = 0;
  for (const m of SPEC_MARKERS) if (t.includes(m)) hits++;
  return hits >= 2;
}

// Заголовок заявки из тела ТЗ.
// ⚠️ СНАЧАЛА ИЩЕМ ПУНКТ «ЧТО ДОРАБОТАТЬ» (Максим, 04.09.2026): раньше бралась
// первая содержательная строка, а ассистент начинает ответ с обращения — в поле
// заголовка попадало «MK, ТЗ:», и заявка приходила без темы.
function guessTitle(text: string): string {
  const чистая = (s: string) => s
    .replace(/^#{1,6}\s*/, "")
    .replace(/^[-*+]\s+/, "")
    .replace(/\*\*/g, "")
    .replace(/`/g, "")
    .trim();
  for (const raw of text.split("\n")) {
    const line = чистая(raw);
    const m = line.match(/^что\s+доработать\s*[:—-]\s*(.+)$/i);
    if (m && m[1].trim().length >= 3) return m[1].trim().replace(/[.;]+$/, "").slice(0, 90);
  }
  // Запасной путь: первая содержательная строка, но не обращение и не «ТЗ:».
  for (const raw of text.split("\n")) {
    const line = чистая(raw);
    if (line.length < 6) continue;
    if (/^[^,]{1,24},?\s*тз\s*:?$/i.test(line)) continue;   // «MK, ТЗ:»
    if (/^(вот|держи|готово|конечно)\b/i.test(line)) continue;
    return line.slice(0, 90);
  }
  return "Доработка";
}

const fieldCls = "field w-full";
// Многострочное поле: `field-area` возвращает скругление темы вместо пилюли.
const fieldAreaCls = "field field-area w-full";

export function AiSpecActions({ text }: { text: string }) {
  const [mode, setMode] = useState<null | "request" | "task">(null);

  // Показываем кнопки оформления ТЗ только на ответах, которые действительно
  // выглядят как ТЗ на доработку, а не на любом ответе ассистента.
  if (!text.trim() || !looksLikeSpec(text)) return null;
  return (
    <>
      <div className="mt-1 flex flex-wrap gap-1.5 pl-1">
        <button type="button" onClick={() => setMode("request")}
          className="glass-flat inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border)] px-2.5 py-1 text-[11px] font-semibold text-[var(--textMuted)] transition hover:text-[var(--text)]">
          <Wrench size={12} /> В доработки
        </button>
        <button type="button" onClick={() => setMode("task")}
          className="glass-flat inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border)] px-2.5 py-1 text-[11px] font-semibold text-[var(--textMuted)] transition hover:text-[var(--text)]">
          <ListTodo size={12} /> Задачей человеку
        </button>
      </div>
      {mode && <SpecModal mode={mode} text={text} onClose={() => setMode(null)} />}
    </>
  );
}

function SpecModal({ mode, text, onClose }: { mode: "request" | "task"; text: string; onClose: () => void }) {
  const [title, setTitle] = useState(() => guessTitle(text));
  const [description, setDescription] = useState(text.trim());
  const [priority, setPriority] = useState<DevRequestPriority>("normal");
  // заявка
  const [departments, setDepartments] = useState<Department[]>([]);
  const [modules, setModules] = useState<PortalModule[]>([]);
  const [deptKey, setDeptKey] = useState("");
  const [topKey, setTopKey] = useState("");
  const [childKey, setChildKey] = useState("");
  // задача
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  // состояние
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        if (mode === "request") {
          const [deps, mods] = await Promise.all([api.getDepartments(), api.getModules()]);
          setDepartments(deps); setModules(mods);
        } else {
          setUsers(await api.chatUsers());
        }
      } catch { /* грузим по возможности; поля можно оставить пустыми */ }
    })();
  }, [mode]);

  const deptModules = useMemo(
    () => modules.filter((m) => (m.departmentKeys ?? []).includes(deptKey)).map((m) => ({ key: m.key, title: m.title })),
    [modules, deptKey],
  );
  const topTabs: TabNode[] = useMemo(() => (deptKey ? buildDeptTabTree(deptKey, deptModules) : []), [deptKey, deptModules]);
  const childTabs: TabNode[] = useMemo(() => topTabs.find((t) => t.key === topKey)?.children ?? [], [topTabs, topKey]);

  const deptOpts: SelectOption[] = useMemo(() => departments.map((d) => ({ value: d.key, label: d.title })), [departments]);
  const topOpts: SelectOption[] = useMemo(() => topTabs.map((t) => ({ value: t.key, label: t.label })), [topTabs]);
  const childOpts: SelectOption[] = useMemo(() => childTabs.map((t) => ({ value: t.key, label: t.label })), [childTabs]);
  const userOpts: SelectOption[] = useMemo(
    () => users.map((u) => ({ value: u.id, label: u.position ? `${u.name} · ${u.position}` : u.name })),
    [users],
  );

  function labelOf(): string {
    const dep = departments.find((d) => d.key === deptKey)?.title ?? "";
    const top = topTabs.find((t) => t.key === topKey)?.label ?? "";
    const child = childTabs.find((t) => t.key === childKey)?.label ?? "";
    return [dep, top, child].filter(Boolean).join(" → ");
  }

  const canSubmit = title.trim().length > 0 && (mode === "request" || assignee) && !saving;

  async function submit() {
    if (!canSubmit) return;
    setSaving(true); setError("");
    try {
      if (mode === "request") {
        await api.createDevRequest({
          dept_key: deptKey || undefined, tab_top: topKey || undefined, tab_child: childKey || undefined,
          tab_label: labelOf() || undefined,
          title: title.trim(), description: description.trim(), priority,
        });
      } else {
        await api.createTask({
          title: title.trim(), description: description.trim(),
          assignee_id: assignee, priority, due_date: due || undefined,
        });
      }
      setDone(true);
      setTimeout(onClose, 900);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  const isReq = mode === "request";

  return (
    <Modal
      size="md"
      // Форма с уже набранным ТЗ: случайный промах мимо панели не должен
      // стирать текст — кит переспросит перед закрытием.
      confirmOnDirtyClose={!done}
      title={
        <span className="flex items-center gap-2">
          {isReq ? <Wrench size={16} /> : <ListTodo size={16} />}
          {isReq ? "В заявки на доработку" : "Поставить задачу человеку"}
        </span>
      }
      onClose={onClose}
      footer={
        done ? null : (
          <>
            <button type="button" onClick={onClose} className="rounded-[var(--radius-md)] px-3 py-1.5 text-[12px] text-[var(--textMuted)] hover:text-[var(--text)]">Отмена</button>
            <button type="button" onClick={() => void submit()} disabled={!canSubmit}
              className="glass-btn-primary inline-flex items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-3.5 py-1.5 text-[12px] font-semibold text-white disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {isReq ? "Создать заявку" : "Поставить задачу"}
            </button>
          </>
        )
      }
    >
      {done ? (
        <div className="flex flex-col items-center gap-2 py-8 text-[12px] text-[var(--text)]">
          <span className="flex h-11 w-11 items-center justify-center rounded-full" style={{ background: "#10b981" }}><Check size={22} color="#fff" /></span>
          {isReq ? "Заявка на доработку создана" : "Задача поставлена"}
        </div>
      ) : (
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold text-[var(--textMuted)]">Название</span>
            <input className={fieldCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Коротко, что доработать" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold text-[var(--textMuted)]">Описание (ТЗ)</span>
            {/* ⚠️ Многострочному полю — `field-area`, а не пилюля `field`: у высокой
                коробки радиус вырастает до половины высоты и боковые дуги съедают
                первые буквы строк (это и было видно на скрине ТЗ). Высота растёт
                под текст, чтобы ТЗ читалось целиком, но не выше 45% экрана —
                дальше поле прокручивается, а окно остаётся с кнопками на виду. */}
            <AutoTextarea
              className={`${fieldAreaCls} resize-y leading-relaxed`}
              style={{ minHeight: 176, maxHeight: "45dvh", overflow: "auto" }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Что доработать, где и как должно быть"
            />
          </label>

          <div>
            <span className="mb-1 block text-[11px] font-semibold text-[var(--textMuted)]">Приоритет</span>
            <div className="flex gap-1.5">
              {PRIOS.map((p) => (
                <button key={p.key} type="button" onClick={() => setPriority(p.key)}
                  className="flex-1 rounded-[var(--radius-md)] border px-2 py-1.5 text-[11px] font-semibold transition"
                  style={priority === p.key
                    ? { borderColor: p.color, color: p.color, background: `color-mix(in srgb, ${p.color} 12%, transparent)` }
                    : { borderColor: "var(--border)", color: "var(--textMuted)" }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {isReq ? (
            <div className="grid grid-cols-1 gap-2">
              <div>
                <span className="mb-1 block text-[11px] font-semibold text-[var(--textMuted)]">Отдел</span>
                <Combobox value={deptKey} onChange={(v) => { setDeptKey(v); setTopKey(""); setChildKey(""); }}
                  options={deptOpts} placeholder="Выберите отдел" searchPlaceholder="Поиск отдела…" clearable className="w-full" />
              </div>
              {topTabs.length > 0 && (
                <div>
                  <span className="mb-1 block text-[11px] font-semibold text-[var(--textMuted)]">Раздел</span>
                  <Select value={topKey} onChange={(v) => { setTopKey(v); setChildKey(""); }}
                    options={topOpts} placeholder="Выберите раздел" className="w-full" />
                </div>
              )}
              {childTabs.length > 0 && (
                <div>
                  <span className="mb-1 block text-[11px] font-semibold text-[var(--textMuted)]">Подраздел</span>
                  <Select value={childKey} onChange={setChildKey}
                    options={childOpts} placeholder="Выберите подраздел" className="w-full" />
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              <div>
                <span className="mb-1 block text-[11px] font-semibold text-[var(--textMuted)]">Исполнитель <span style={{ color: "var(--danger)" }}>*</span></span>
                <Combobox value={assignee} onChange={setAssignee} options={userOpts}
                  placeholder="Выберите исполнителя" searchPlaceholder="Поиск по имени…" className="w-full" />
              </div>
              <div>
                <span className="mb-1 block text-[11px] font-semibold text-[var(--textMuted)]">Срок (необязательно)</span>
                <DatePicker value={due} onChange={setDue} placeholder="Выбрать дату" clearable className="w-full" />
              </div>
            </div>
          )}

          {error && <p className="text-[11px] text-[var(--danger)]">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
